<template>
  <div class="cashier-desktop cashier-desktop--alipay">
    <div class="cashier-desktop__shell cashier-desktop__shell--split">
      <aside class="cashier-desktop__overview">
        <div class="cashier-desktop__badge">
          <span class="cashier-desktop__badge-dot"></span>
          支付宝
        </div>
        <div class="cashier-desktop__amount">¥{{ formattedAmount }}</div>
        <h1 class="cashier-desktop__title">{{ payOrderInfo.subject || '支付宝支付订单' }}</h1>
        <div class="cashier-desktop__subtitle">{{ payOrderInfo.mchName || '商户信息加载中' }}</div>
        <div v-if="payStatusText" class="cashier-desktop__status-chip">{{ payStatusText }}</div>

        <div class="cashier-desktop__overview-grid">
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">支付方式</div>
            <div class="cashier-desktop__meta-value">{{ methodName }}</div>
          </div>
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">订单状态</div>
            <div class="cashier-desktop__meta-value cashier-desktop__meta-value--status">{{ orderStateText }}</div>
          </div>
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">订单号</div>
            <div class="cashier-desktop__meta-value">{{ payOrderInfo.payOrderId || '--' }}</div>
          </div>
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">商户单号</div>
            <div class="cashier-desktop__meta-value">{{ payOrderInfo.mchOrderNo || '--' }}</div>
          </div>
        </div>

        <div class="cashier-desktop__overview-copy">
          <strong>{{ overviewPayHint }}</strong>
        </div>
      </aside>

      <section class="cashier-desktop__workspace">
        <div class="cashier-desktop__workspace-main">
          <div class="cashier-desktop__section-head">
            <div>
              <h2 class="cashier-desktop__section-title">请完成支付</h2>
            </div>
          </div>

          <div class="cashier-desktop__qr-card">
            <div>
              <h3 class="cashier-desktop__qr-title">{{ workspaceTitle }}</h3>
              <p class="cashier-desktop__qr-copy">{{ workspaceCopy }}</p>
            </div>

            <div class="cashier-desktop__qr-stage">
              <div class="cashier-desktop__qr-frame">
                <template v-if="qrCodeUrl">
                  <img v-if="qrCodeImageMode" :src="qrCodeUrl" alt="支付宝支付二维码" />
                  <div v-else ref="qrcode"></div>
                </template>
                <div v-else class="cashier-desktop__placeholder">
                  <div class="cashier-desktop__placeholder-mark">¥</div>
                  <div class="cashier-desktop__placeholder-title">{{ placeholderTitle }}</div>
                  <div class="cashier-desktop__placeholder-copy">{{ placeholderCopy }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="cashier-desktop__workspace-side">
          <div class="cashier-desktop__summary">
            <div class="cashier-desktop__summary-head">
              <h3 class="cashier-desktop__card-title">订单摘要</h3>
            </div>
            <div class="cashier-desktop__summary-row">
              <span class="cashier-desktop__summary-key">订单号</span>
              <span class="cashier-desktop__summary-value">{{ payOrderInfo.payOrderId || '--' }}</span>
            </div>
            <div class="cashier-desktop__summary-row">
              <span class="cashier-desktop__summary-key">商户单号</span>
              <span class="cashier-desktop__summary-value">{{ payOrderInfo.mchOrderNo || '--' }}</span>
            </div>
            <div class="cashier-desktop__summary-row">
              <span class="cashier-desktop__summary-key">状态</span>
              <span class="cashier-desktop__summary-value">{{ orderStateText }}</span>
            </div>
          </div>

          <div v-if="productItems.length > 0" class="cashier-desktop__products">
            <div class="cashier-desktop__products-head">
              <h3 class="cashier-desktop__card-title">商品明细</h3>
            </div>

            <div class="cashier-desktop__product-list">
              <div class="cashier-desktop__product-item" v-for="(item, index) in productItems" :key="index">
                <div>
                  <div class="cashier-desktop__product-name">{{ item.name }}</div>
                  <div class="cashier-desktop__product-count">x{{ item.quantity }}</div>
                </div>
                <div class="cashier-desktop__product-price">¥{{ formatAmount(item.price) }}</div>
              </div>
            </div>
          </div>

          <div class="cashier-desktop__actions">
            <div class="cashier-desktop__actions-head">
              <h3 class="cashier-desktop__card-title">操作</h3>
            </div>

            <div class="cashier-desktop__actions-row">
              <button class="cashier-desktop__primary-btn" :disabled="isPaySubmitting" @click="pay">
                {{ actionButtonText }}
              </button>
              <button class="cashier-desktop__secondary-btn" type="button" @click="$router.push({ name: 'CashierHome' })">
                返回选择
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcodejs2'
import { getPayPackage, getPayOrderInfo, getRedirectUrl } from '@/api/api'
import { getCachedPayData, removeCachedPayData, setCachedPayData } from '@/utils/cashierPayCache'
import channelUserIdUtil from '@/utils/channelUserId'
import wayCodeUtil from '@/utils/wayCode'
import config from '@/config'

export default {
  data: function () {
    return {
      amount: null,
      resData: {},
      payOrderInfo: {},
      productItems: [],
      qrCodeUrl: null,
      qrCodeImageMode: false,
      isPaySubmitting: false,
      payStatusText: '',
      statusPollTimer: null,
      isSuccessHandled: false
    }
  },
  computed: {
    currentWayCode() {
      const payWay = wayCodeUtil.getPayWay()
      return payWay ? payWay.wayCode : 'ALI_PC'
    },
    selectedAlipayLaunchMode() {
      return wayCodeUtil.getSelectedAlipayLaunchMode() || 'wap'
    },
    methodName() {
      if (this.currentWayCode === 'ALI_JSAPI') {
        return '支付宝内支付'
      }
      if (this.currentWayCode === 'ALI_WAP') {
        return this.selectedAlipayLaunchMode === 'app' ? '支付宝APP支付' : '支付宝网页支付'
      }
      if (this.currentWayCode === 'ALI_PC') {
        return '支付宝在线付款'
      }
      return '支付宝扫码支付'
    },
    isQrPayMode() {
      return this.currentWayCode === 'ALI_PC' || this.currentWayCode === 'ALI_QR'
    },
    overviewPayHint() {
      if (this.isQrPayMode) {
        return this.currentWayCode === 'ALI_PC' ? '请在支付宝在线收银台完成支付。' : '请使用支付宝扫码完成支付。'
      }
      if (this.currentWayCode === 'ALI_WAP') {
        return this.selectedAlipayLaunchMode === 'app'
          ? '请点击按钮打开支付宝APP，若未打开会自动进入网页支付。'
          : '请在支付宝网页收银台中完成支付。'
      }
      return '请在支付宝中确认支付。'
    },
    workspaceTitle() {
      if (this.isQrPayMode) {
        return this.currentWayCode === 'ALI_PC' ? '打开支付宝在线付款' : '扫描二维码支付'
      }
      if (this.currentWayCode === 'ALI_WAP') {
        return this.selectedAlipayLaunchMode === 'app' ? '打开支付宝APP支付' : '正在打开支付宝网页支付'
      }
      return '正在发起支付宝支付'
    },
    workspaceCopy() {
      if (this.isQrPayMode) {
        return this.currentWayCode === 'ALI_PC' ? '支付请求创建后会进入支付宝在线收银台。' : '请打开支付宝扫一扫二维码完成付款。'
      }
      if (this.currentWayCode === 'ALI_WAP') {
        return this.selectedAlipayLaunchMode === 'app'
          ? '点击下方按钮后会优先打开支付宝APP。'
          : '支付请求创建后会进入手机网页收银台。'
      }
      return '支付请求创建后会自动继续。'
    },
    formattedAmount() {
      return this.formatAmount(this.payOrderInfo.amount)
    },
    orderStateText() {
      const state = this.payOrderInfo.state
      if (state === 2 || state === '2') return '支付成功'
      if (state === 1 || state === '1') return '支付中'
      if (state === 3 || state === '3') return '支付失败'
      if (state === 6 || state === '6') return '订单关闭'
      return '待支付'
    },
    placeholderTitle() {
      if (this.currentWayCode === 'ALI_JSAPI') {
        return '正在进入支付宝支付'
      }
      if (this.currentWayCode === 'ALI_WAP') {
        return this.selectedAlipayLaunchMode === 'app' ? '等待打开支付宝APP' : '正在进入网页支付'
      }
      return '二维码生成中'
    },
    placeholderCopy() {
      if (this.currentWayCode === 'ALI_JSAPI') {
        return '即将打开支付宝支付'
      }
      if (this.currentWayCode === 'ALI_WAP') {
        return this.selectedAlipayLaunchMode === 'app' ? '点击后会尝试打开支付宝APP，失败时自动使用网页支付' : '即将打开支付宝网页收银台'
      }
      return '二维码准备完成后即可扫码'
    },
    actionButtonText() {
      if (this.isPaySubmitting) {
        return '处理中...'
      }
      if (this.currentWayCode === 'ALI_JSAPI') {
        return '重新发起支付'
      }
      if (this.currentWayCode === 'ALI_WAP') {
        const isPaying = this.payOrderInfo.state === 1 || this.payOrderInfo.state === '1'
        if (this.selectedAlipayLaunchMode === 'app') {
          return isPaying ? '继续打开支付宝APP' : '打开支付宝APP支付'
        }
        return isPaying ? '继续网页支付' : '打开网页支付'
      }
      return '刷新二维码'
    }
  },
  mounted() {
    this.setPayOrderInfo()
  },
  beforeDestroy() {
    this.stopStatusPolling()
  },
  methods: {
    formatAmount(amount) {
      if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
        return '--'
      }
      return (Number(amount) / 100).toFixed(2)
    },

    syncProductItems(extParam) {
      this.productItems = []
      if (!extParam) return
      try {
        const extData = JSON.parse(extParam)
        if (Array.isArray(extData.items)) {
          this.productItems = extData.items
        }
      } catch (e) {
        console.error('解析商品明细失败', e)
      }
    },

    setPayOrderInfo() {
      const that = this
      getPayOrderInfo().then(res => {
        that.payOrderInfo = res
        that.syncProductItems(res.extParam)

        if (res.amount) {
          that.amount = res.amount / 100
          if (res.state === 2 || res.state === '2') {
            that.clearCachedPayData()
            that.handlePaySuccess(res)
          } else if (res.state === 1 || res.state === '1') {
            if (that.restoreCachedPayData({ allowRedirect: that.currentWayCode === 'ALI_WAP' && !that.shouldWaitForUserGesture() })) {
              return
            }
            that.startStatusPolling()
            that.payStatusText = that.isQrPayMode
              ? '当前订单支付中，请继续扫码支付'
              : '支付请求已创建，请继续完成支付'
          } else if (res.state === 3 || res.state === '3' || res.state === 6 || res.state === '6') {
            that.clearCachedPayData()
            that.payStatusText = res.errMsg || '订单状态异常'
          } else if (res.state === 0 || res.state === '0' || res.state === undefined || res.state === null) {
            if (that.shouldWaitForUserGesture()) {
              that.payStatusText = '请点击按钮打开支付宝APP'
              return
            }
            that.pay()
          }
        }
      }).catch(res => {
        that.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      })
    },

    pay: function () {
      if (this.isPaySubmitting) {
        return
      }

      if (isNaN(this.amount) || this.amount <= 0) {
        this.payStatusText = '请输入有效支付金额'
        return alert('请输入金额')
      }

      const currentWayCode = this.currentWayCode
      if (this.isQrPayMode && (this.payOrderInfo.state === 1 || this.payOrderInfo.state === '1')) {
        if (this.restoreCachedPayData()) {
          this.payStatusText = '已恢复当前订单二维码'
        } else {
          this.startStatusPolling()
          this.payStatusText = '当前订单已支付中，请勿重复发起支付'
        }
        return
      }

      if (currentWayCode === 'ALI_WAP' && (this.payOrderInfo.state === 1 || this.payOrderInfo.state === '1')) {
        if (this.restoreCachedPayData({ allowRedirect: true })) {
          return
        }
        this.startStatusPolling()
        this.payStatusText = '当前订单支付中，请等待支付结果'
        return
      }

      if (wayCodeUtil.requiresChannelUserId(currentWayCode) && !channelUserIdUtil.getChannelUserId()) {
        this.payStatusText = '正在跳转支付宝授权环境...'
        return getRedirectUrl().then(url => {
          window.location.href = url
        }).catch(res => {
          this.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
        })
      }

      const that = this
      that.isPaySubmitting = true
      that.qrCodeUrl = null
      that.qrCodeImageMode = false
      that.payStatusText = currentWayCode === 'ALI_JSAPI'
        ? '正在创建支付请求...'
        : currentWayCode === 'ALI_WAP' ? '正在创建手机网站支付请求...' : '正在生成二维码...'
      that.isSuccessHandled = false
      that.stopStatusPolling()
      if (that.$refs.qrcode) {
        that.$refs.qrcode.innerHTML = ''
      }

      getPayPackage(this.amount).then(res => {
        if (String(res.code) !== '0') {
          that.payStatusText = res.msg || '创建支付请求失败'
          return alert(res.msg)
        }

        if (Number(res.data.orderState) !== 1) {
          that.payStatusText = res.data.errMsg || '订单状态异常'
          return alert(res.data.errMsg)
        }

        const normalizedPayData = that.normalizePayData(res.data)
        if (that.handlePayData(normalizedPayData)) {
          return
        }

        that.payStatusText = '正在等待支付宝确认...'
        if (!window.AlipayJSBridge) {
          document.addEventListener('AlipayJSBridgeReady', function () {
            that.doAlipay(res.data.alipayTradeNo)
          }, false)
        } else {
          that.doAlipay(res.data.alipayTradeNo)
        }
      }).catch(res => {
        that.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      }).finally(() => {
        that.isPaySubmitting = false
      })
    },

    normalizePayData(payData) {
      if (!payData) return null
      const normalized = Object.assign({}, payData)
      normalized.payDataType = normalized.payDataType || (
        normalized.codeImgUrl ? 'codeImgUrl'
          : normalized.codeUrl ? 'codeUrl'
            : normalized.payUrl ? 'payurl'
              : normalized.formContent ? 'form' : null
      )
      normalized.payData = normalized.payData || normalized.codeImgUrl || normalized.codeUrl || normalized.payUrl || normalized.formContent || null
      return normalized
    },

    getPayCacheKey() {
      const payOrderId = this.payOrderInfo.payOrderId || config.cacheToken
      return payOrderId ? `${this.currentWayCode}:${payOrderId}` : null
    },

    shouldWaitForUserGesture() {
      return this.currentWayCode === 'ALI_WAP' && this.selectedAlipayLaunchMode === 'app'
    },

    restoreCachedPayData(options = {}) {
      const canRedirectPayUrl = options.allowRedirect === true
      if (!this.isQrPayMode && !(this.currentWayCode === 'ALI_WAP' && canRedirectPayUrl)) {
        return false
      }

      const cacheKey = this.getPayCacheKey()
      const payData = getCachedPayData(cacheKey) || this.getPersistedPayData()
      if (!payData) {
        return false
      }

      return this.handlePayData(this.normalizePayData(payData))
    },

    getPersistedPayData() {
      if (!this.payOrderInfo.channelExtra) {
        return null
      }

      try {
        const channelExtra = JSON.parse(this.payOrderInfo.channelExtra)
        return channelExtra && channelExtra.cashierPayData ? channelExtra.cashierPayData : null
      } catch (e) {
        return null
      }
    },

    clearCachedPayData() {
      removeCachedPayData(this.getPayCacheKey())
    },

    handlePayData(payData) {
      if (!payData || !payData.payDataType) {
        return false
      }

      if (payData.payDataType === 'codeUrl' || payData.payDataType === 'codeImgUrl') {
        if (this.isQrPayMode) {
          setCachedPayData(this.getPayCacheKey(), payData)
        }
        this.qrCodeUrl = payData.payData
        this.qrCodeImageMode = payData.payDataType === 'codeImgUrl'
        this.payStatusText = '等待支付完成...'
        this.startStatusPolling()
        this.$nextTick(() => {
          this.renderQrCode()
        })
        return true
      }

      if (payData.payDataType === 'payurl') {
        if (this.currentWayCode === 'ALI_WAP') {
          setCachedPayData(this.getPayCacheKey(), payData)
        }
        this.handlePayUrl(payData.payData)
        return true
      }

      if (payData.payDataType === 'form') {
        this.payStatusText = '正在提交支付...'
        this.submitPayForm(payData.payData)
        return true
      }

      return false
    },

    handlePayUrl(payUrl) {
      if (this.currentWayCode === 'ALI_WAP') {
        this.startStatusPolling()
      }

      if (this.currentWayCode === 'ALI_WAP' && this.selectedAlipayLaunchMode === 'app' && wayCodeUtil.isMobileBrowser()) {
        this.openAlipayAppPay(payUrl)
        return
      }

      this.payStatusText = this.currentWayCode === 'ALI_WAP'
        ? '正在跳转支付宝网页支付...'
        : '正在跳转支付宝支付页面...'
      window.location.href = payUrl
    },

    buildAlipayAppUrl(payUrl) {
      return `alipays://platformapi/startapp?appId=20000067&url=${encodeURIComponent(payUrl)}`
    },

    openAlipayAppPay(payUrl) {
      this.payStatusText = '正在打开支付宝APP...'
      const fallbackTimer = window.setTimeout(() => {
        if (!document.hidden) {
          this.payStatusText = '正在切换到支付宝网页支付...'
          window.location.href = payUrl
        }
      }, 1600)

      const cancelFallback = () => {
        if (document.hidden) {
          window.clearTimeout(fallbackTimer)
        }
        document.removeEventListener('visibilitychange', cancelFallback)
      }

      document.addEventListener('visibilitychange', cancelFallback)
      window.location.href = this.buildAlipayAppUrl(payUrl)
    },

    renderQrCode() {
      if (this.qrCodeImageMode || !this.qrCodeUrl || !this.$refs.qrcode) {
        return
      }
      this.$refs.qrcode.innerHTML = ''
      new QRCode(this.$refs.qrcode, {
        text: this.qrCodeUrl,
        width: 256,
        height: 256
      })
    },

    submitPayForm(formHtml) {
      const formWrapper = document.createElement('div')
      formWrapper.style.display = 'none'
      formWrapper.innerHTML = formHtml
      document.body.appendChild(formWrapper)
      const form = formWrapper.querySelector('form')
      if (form) {
        form.submit()
      }
    },

    startStatusPolling() {
      this.stopStatusPolling()
      this.statusPollTimer = setInterval(() => {
        this.refreshPayOrderStatus()
      }, 2000)
    },

    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer)
        this.statusPollTimer = null
      }
    },

    refreshPayOrderStatus() {
      getPayOrderInfo().then(res => {
        this.payOrderInfo = Object.assign({}, this.payOrderInfo, res)
        if (res.state === 2 || res.state === '2') {
          this.handlePaySuccess(res)
          return
        }
        if (res.state === 3 || res.state === '3' || res.state === 6 || res.state === '6') {
          this.stopStatusPolling()
          this.clearCachedPayData()
          this.payStatusText = res.errMsg || '订单状态异常'
        }
      }).catch(() => {
        this.payStatusText = '支付结果查询中...'
      })
    },

    buildReturnUrlWithOrderInfo(returnUrl) {
      if (!returnUrl) return returnUrl
      try {
        const url = new URL(returnUrl)
        if (this.payOrderInfo.mchOrderNo) {
          url.searchParams.set('bizOrderNo', this.payOrderInfo.mchOrderNo)
        }
        if (this.payOrderInfo.payOrderId) {
          url.searchParams.set('payOrderId', this.payOrderInfo.payOrderId)
        }
        return url.toString()
      } catch (e) {
        return returnUrl
      }
    },

    redirectAfterSuccess(returnUrl) {
      const targetUrl = this.buildReturnUrlWithOrderInfo(returnUrl)
      if (targetUrl) {
        window.setTimeout(() => {
          window.location.href = targetUrl
        }, 600)
      }
    },

    handlePaySuccess(orderInfo) {
      if (this.isSuccessHandled) {
        return
      }
      this.isSuccessHandled = true
      this.stopStatusPolling()
      this.clearCachedPayData()
      this.payStatusText = '支付成功，正在返回原页面...'
      this.redirectAfterSuccess(orderInfo && orderInfo.returnUrl)
    },

    doAlipay(alipayTradeNo) {
      const that = this
      window.AlipayJSBridge.call('tradePay', {
        tradeNO: alipayTradeNo
      }, function (data) {
        if (data.resultCode === '9000') {
          that.payStatusText = '支付成功，正在返回原页面...'
          if (that.payOrderInfo.returnUrl) {
            that.redirectAfterSuccess(that.payOrderInfo.returnUrl)
          } else {
            window.AlipayJSBridge.call('closeWebview')
          }
        } else if (data.resultCode === '8000' || data.resultCode === '6004') {
          that.payStatusText = '支付处理中...'
          alert(JSON.stringify(data))
          window.AlipayJSBridge.call('closeWebview')
        } else {
          that.payStatusText = '用户已取消支付'
          alert('用户已取消！')
          window.AlipayJSBridge.call('closeWebview')
        }
      })
    }
  }
}
</script>

<style scoped>
@import '../cashier-desktop.css';
</style>
